/**
 * Generates a list of options with category separators for use in dropdowns and selects
 * @param {Array} options - The list of options or values to process
 * @param {Array} columns - Optional array of column definitions with field, label, definition, and category properties
 * @param {string} searchText - Optional search text to filter the options
 * @param {Function} sortFn - Optional function to sort items within each category
 * @returns {Array} Array of options with category separators
 */
export function getOptionsWithSeparators (options, columns = [], searchText = '', sortFn = null) {
  const itemOptions = options.map(value => {
    const columnOption = columns.find(col => col.field === (value?.value || value))

    if (columnOption?.hide) {
      return null
    }

    return {
      ...value, // Preserve any existing properties
      value: value?.value || value,
      label: columnOption?.label || value?.label || _.startCase(value?.value || value),
      definition: columnOption?.definition || value?.definition,
      category: columnOption?.category || value?.category || 'Other',
      key: `option-${value?.value || value}`,
      isSeparator: false
    }
  })

  // Filter out null values and filter by search if provided
  let filteredOptions = itemOptions.filter(item => item !== null)

  if (searchText) {
    const searchLower = searchText.toLowerCase()
    filteredOptions = filteredOptions.filter(item => {
      const label = item?.label || _.startCase(item?.value || item)
      const category = item?.category || 'Other'
      return label.toLowerCase().includes(searchLower) ||
        category.toLowerCase().includes(searchLower)
    })
  }

  // Create a 2D array where each inner array represents a category and its options
  const categoryGroups = _.reduce(filteredOptions, (acc, option) => {
    const category = option.category
    const categoryIndex = _.findIndex(acc, group => group[0]?.category === category)

    if (categoryIndex === -1) {
      // New category - add new group with separator and option
      acc.push([
        {
          label: category,
          category,
          isSeparator: true,
          key: `separator-${category}`
        },
        option
      ])
    } else {
      // Existing category - add option to existing group
      acc[categoryIndex].push(option)
    }

    return acc
  }, [])

  // Sort items within each category if a sort function is provided
  if (sortFn) {
    categoryGroups.forEach(group => {
      const [separator, ...items] = group
      const sortedItems = sortFn(items)
      group.length = 0
      group.push(separator, ...sortedItems)
    })
  }

  // Flatten 2d array while preserving order
  return _.flatten(categoryGroups)
}
